import { memo } from 'react';
import { cn } from '@/utils/cn';

export interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'white' | 'action';
  fullPage?: boolean;
}

const SIZE_PX = { sm: 16, md: 24, lg: 40 } as const;

const STROKE_COLOR = {
  primary: 'text-primary',
  white: 'text-white',
  action: 'text-action',
} as const;

const SpinnerInner = memo(function SpinnerInner({
  size = 'md',
  color = 'primary',
}: Pick<SpinnerProps, 'size' | 'color'>): React.ReactElement {
  const px = SIZE_PX[size];
  return (
    <svg
      className={cn('animate-spin', STROKE_COLOR[color])}
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
});

export const Spinner = memo(function Spinner({
  size = 'md',
  color = 'primary',
  fullPage = false,
}: SpinnerProps): React.ReactElement {
  if (fullPage) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-white/80"
        role="status"
        aria-label="Loading"
      >
        <SpinnerInner size={size} color={color} />
      </div>
    );
  }
  return (
    <span role="status" aria-label="Loading">
      <SpinnerInner size={size} color={color} />
    </span>
  );
});
